import type {
  ApiRequestConfig,
  AssignPermissionSetDto,
  CreatePermissionDefinitionDto,
  CreatePermissionSetDto,
  ModuleTypeEnum,
  PermissionAssignment,
  PermissionDefinition,
  PermissionSet,
  PermissionSetAssignment,
  UpdatePermissionDefinitionDto,
  UpdatePermissionSetDto,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

// Extended types matching store types
export interface ExtendedPermissionSet extends PermissionSet {
  permissionSetItems?: Array<{
    id?: string
    permissionSetId: string
    permissionDefinitionId: string
    permissionDefinition?: PermissionDefinition
  }>
}

interface ExtendedPermissionAssignment extends PermissionAssignment {
  permissionDefinition?: PermissionDefinition
}

interface ExtendedPermissionSetAssignment extends PermissionSetAssignment {
  permissionSet?: ExtendedPermissionSet
}

export interface MemberPermissionsResponse {
  directPermissions: ExtendedPermissionAssignment[]
  permissionSets: ExtendedPermissionSetAssignment[]
}

class PermissionsApiService extends BaseApi<
  PermissionDefinition,
  CreatePermissionDefinitionDto,
  UpdatePermissionDefinitionDto
> {
  constructor() {
    super({
      baseEndpoint: '/permission-definitions',
      responseNormalization: true,
    })
  }

  // Permission Definitions
  async getDefinitions(
    moduleType?: ModuleTypeEnum,
    _forceRefresh?: boolean,
    config?: ApiRequestConfig,
  ): Promise<PermissionDefinition[]> {
    const params = moduleType ? `?moduleType=${moduleType}` : ''
    return this.makeRequest<PermissionDefinition[]>(
      `${this.config.baseEndpoint}${params}`,
      { ...config },
    )
  }

  async createDefinition(
    data: CreatePermissionDefinitionDto,
  ): Promise<PermissionDefinition> {
    return this.makeRequest<PermissionDefinition>(this.config.baseEndpoint, {
      method: 'POST',
      body: data,
    })
  }

  async updateDefinition(
    id: string,
    data: UpdatePermissionDefinitionDto,
  ): Promise<PermissionDefinition> {
    return this.makeRequest<PermissionDefinition>(
      `${this.config.baseEndpoint}/${id}`,
      { method: 'PUT', body: data },
    )
  }

  async deleteDefinition(id: string): Promise<void> {
    await this.makeRequest(`${this.config.baseEndpoint}/${id}`, {
      method: 'DELETE',
    })
  }

  // Permission Sets
  async getPermissionSets(
    organizationId?: string,
    _forceRefresh?: boolean,
    config?: ApiRequestConfig,
  ): Promise<PermissionSet[]> {
    return this.makeRequest<PermissionSet[]>('/permission-sets', {
      ...config,
      headers: {
        ...config?.headers,
        ...(organizationId ? { 'x-organization-id': organizationId } : {}),
      },
    })
  }

  async getMemberPermissionDetails(
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<MemberPermissionsResponse> {
    const response = await this.makeRequest<Partial<MemberPermissionsResponse>>(
      `/permissions/members/${encodeURIComponent(memberId)}`,
      { ...config },
    )
    return {
      directPermissions: response.directPermissions || [],
      permissionSets: response.permissionSets || [],
    }
  }

  async createPermissionSet(
    data: CreatePermissionSetDto,
  ): Promise<PermissionSet> {
    return this.makeRequest<PermissionSet>('/permission-sets', {
      method: 'POST',
      body: data,
    })
  }

  async updatePermissionSet(
    id: string,
    data: UpdatePermissionSetDto,
  ): Promise<PermissionSet> {
    return this.makeRequest<PermissionSet>(`/permission-sets/${id}`, {
      method: 'PUT',
      body: data,
    })
  }

  async deletePermissionSet(id: string): Promise<void> {
    await this.makeRequest(`/permission-sets/${id}`, { method: 'DELETE' })
  }

  // Member Permissions
  async getMemberPermissions(
    memberId: string,
  ): Promise<PermissionSetAssignment[]> {
    const response = await this.makeRequest<{
      permissionSets: PermissionSetAssignment[]
    }>(`/members/${memberId}/permissions`)
    return response.permissionSets || []
  }

  async assignPermissionSet(data: AssignPermissionSetDto): Promise<void> {
    await this.makeRequest('/permission-sets/assign', {
      method: 'POST',
      body: data,
    })
  }

  async removePermissionSet(
    memberId: string,
    permissionSetId: string,
  ): Promise<void> {
    await this.makeRequest(
      `/permission-sets/assignments/${memberId}/${permissionSetId}`,
      { method: 'DELETE' },
    )
  }

  async updateMemberPermissions(
    memberId: string,
    permissionSetIds: string[],
  ): Promise<void> {
    await this.makeRequest(`/members/${memberId}/permissions`, {
      method: 'PUT',
      body: { permissionSetIds },
    })
  }

  // Organization Initialization
  async initializeOrganizationPermissions(
    organizationId: string,
  ): Promise<void> {
    await this.makeRequest(
      `/permissions/organizations/${organizationId}/initialize`,
      { method: 'POST' },
    )
  }

  async createDefaultPermissions(organizationId: string): Promise<void> {
    await this.makeRequest(
      `/permissions/organizations/${organizationId}/create-defaults`,
      { method: 'POST' },
    )
  }

  // Legacy cache method (no-op - TanStack Query handles caching)
  clearCache(): void {
    /* no-op */
  }
}

// Export singleton instance
export const permissionsApi = createSingletonApi(PermissionsApiService)
